/**
 * Turning a signature into the small, transparent PNG an invoice prints.
 *
 * Most people photograph their signature on white paper, and a white box
 * printed over the invoice's own paper looks pasted on. So an upload is, by
 * default, made transparent where it is paper-white, trimmed to the ink with a
 * little margin, and scaled to at most MAX_WIDTH × MAX_HEIGHT. A drawn
 * signature is already transparent and is only trimmed.
 *
 * The pixel rules (`clearPaper`, `inkBounds`) work on plain ImageData-shaped
 * objects, so they are tested without a canvas.
 */

export const MAX_WIDTH = 600;
export const MAX_HEIGHT = 220;
export const MAX_BYTES = 1024 * 1024; // the server's limit
const WORKING_EDGE = 1600; // an 12-megapixel phone photo is shrunk to this first
const PADDING = 8;

/**
 * Make paper-white pixels transparent, in place. Pixels lighter than `paper`
 * vanish; between `ink` and `paper` they fade, so the stroke edge stays smooth
 * instead of turning jagged.
 */
export function clearPaper(imageData, { paper = 225, ink = 150 } = {}) {
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (lum >= paper) data[i + 3] = 0;
    else if (lum > ink) data[i + 3] = Math.round(data[i + 3] * ((paper - lum) / (paper - ink)));
  }
  return imageData;
}

/** The smallest box holding every pixel with alpha above `minAlpha`, or null when there is no ink. */
export function inkBounds(imageData, minAlpha = 24) {
  const { data, width, height } = imageData;
  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > minAlpha) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }
  if (right < 0) return null;
  return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

function canvasOf(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

const toBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

export function loadImageFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("That file isn't an image we can read.")); };
    img.src = url;
  });
}

/**
 * The PNG to upload, from an image or a canvas: `{ blob, width, height }`.
 * Throws a sentence when there is nothing to keep.
 */
export async function prepareSignature(source, { removeBackground = true } = {}) {
  const srcW = source.naturalWidth || source.width;
  const srcH = source.naturalHeight || source.height;
  if (!srcW || !srcH) throw new Error("That image is empty.");

  const shrink = Math.min(1, WORKING_EDGE / Math.max(srcW, srcH));
  const work = canvasOf(srcW * shrink, srcH * shrink);
  const ctx = work.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(source, 0, 0, work.width, work.height);

  const pixels = ctx.getImageData(0, 0, work.width, work.height);
  if (removeBackground) {
    clearPaper(pixels);
    ctx.putImageData(pixels, 0, 0);
  }
  const box = inkBounds(pixels);
  if (!box) throw new Error("We couldn't find a signature in that image — is the ink dark enough?");

  const x = Math.max(0, box.x - PADDING);
  const y = Math.max(0, box.y - PADDING);
  const w = Math.min(work.width - x, box.width + PADDING * 2);
  const h = Math.min(work.height - y, box.height + PADDING * 2);

  let scale = Math.min(1, MAX_WIDTH / w, MAX_HEIGHT / h);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const out = canvasOf(w * scale, h * scale);
    const octx = out.getContext("2d");
    octx.imageSmoothingQuality = "high";
    octx.drawImage(work, x, y, w, h, 0, 0, out.width, out.height);
    // eslint-disable-next-line no-await-in-loop
    const blob = await toBlob(out);
    if (blob && blob.size <= MAX_BYTES) return { blob, width: out.width, height: out.height };
    scale *= 0.7;
  }
  throw new Error("That signature is too large to use. Try a smaller image.");
}
